import {
  ArrowDropDownRounded,
  ChevronRightRounded,
  DescriptionRounded,
  Logout,
  Menu as MenuIcon,
  Payments,
  PeopleAltRounded,
  RocketLaunchRounded,
  Subscriptions,
  VerifiedUserRounded,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Breadcrumbs,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import AppVersionBadge from "../components/tools/AppVersionBadge";
import { drawerWidth } from "../config/config";
import { getInitials } from "../utils/StringTool";
import AdminLayout from "./AdminLayout";
import { useDashboard } from "./hooks/useDashboard";

type DashboardNavItem = {
  text: string;
  icon: React.ReactElement;
  path: string;
  section: string;
  title: string;
  subtitle: string;
};

type DashboardNavGroup = {
  title: string;
  items: DashboardNavItem[];
};

const menuGroups: DashboardNavGroup[] = [
  {
    title: "Inicio",
    items: [
      {
        text: "Mi Kit",
        icon: <RocketLaunchRounded />,
        path: "/dashboard/plan",
        section: "Inicio",
        title: "Mi Kit",
        subtitle: "Recursos y formularios",
      },
    ],
  },
  {
    title: "Gestión",
    items: [
      {
        text: "Usuarios",
        icon: <PeopleAltRounded />,
        path: "/dashboard/admin/users",
        section: "Gestión",
        title: "Usuarios",
        subtitle: "Listado general",
      },
      {
        text: "Planes",
        icon: <Subscriptions />,
        path: "/dashboard/admin/plans",
        section: "Gestión",
        title: "Planes",
        subtitle: "Capacidades y recursos",
      },
      {
        text: "Cuestionarios",
        icon: <DescriptionRounded />,
        path: "/dashboard/admin/questionnaires",
        section: "Cuestionarios",
        title: "Listado General",
        subtitle: "Editor y versiones",
      },
      {
        text: "Pagos",
        icon: <Payments />,
        path: "/dashboard/admin/payments",
        section: "Gestión",
        title: "Pagos",
        subtitle: "Registro y trazabilidad",
      },
    ],
  },
  {
    title: "Mi Perfil",
    items: [
      {
        text: "Datos personales",
        icon: <VerifiedUserRounded />,
        path: "/dashboard/profile",
        section: "Mi Perfil",
        title: "Datos Personales",
        subtitle: "Información de cuenta",
      },
    ],
  },
];

const getPageMeta = (pathname: string, visibleItems: DashboardNavItem[]) => {
  return (
    visibleItems.find(
      (item) => pathname === item.path || pathname.startsWith(`${item.path}/`),
    ) ?? visibleItems[0]
  );
};

const Dashboard: React.FC = () => {
  const dashboard = useDashboard();
  const {
    user,
    userData,
    permittedPaths,
    isMobile,
    mobileOpen,
    dialogOpen,
    location,
    theme,
    userMenuOpen,
    userMenuAnchor,
    handleUserMenuOpen,
    handleUserMenuClose,
    setMobileOpen,
    setDialogOpen,
    handleDrawerToggle,
    handleLogout,
  } = dashboard;
  const isAdmin = (userData?.role ?? "").toUpperCase() === "ADMIN";

  const visibleGroups = useMemo(
    () =>
      menuGroups
        .map((group) => ({
          ...group,
          items: group.items.filter(({ path }) => permittedPaths.includes(path)),
        }))
        .filter((group) => group.items.length > 0),
    [permittedPaths],
  );

  const visibleItems = useMemo(
    () => visibleGroups.flatMap((group) => group.items),
    [visibleGroups],
  );

  const pageMeta = useMemo(
    () => getPageMeta(location.pathname, visibleItems),
    [location.pathname, visibleItems],
  );

  const userLabel = userData?.fullName || "Usuario";
  const userContact = user?.email || user?.phoneNumber || "Cuenta";

  if (isAdmin) {
    return <AdminLayout />;
  }

  const menuProfile = (
    <Menu
      anchorEl={userMenuAnchor}
      open={userMenuOpen}
      onClose={handleUserMenuClose}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 240,
          borderRadius: 3,
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.16)",
          border: "1px solid rgba(15, 23, 42, 0.08)",
        },
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="body2" fontWeight={700}>
          {userLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {userContact}
        </Typography>
      </Box>
      <Divider />
      <MenuItem
        onClick={() => {
          handleUserMenuClose();
          setDialogOpen(true);
        }}
        sx={{ py: 1.2 }}
      >
        <ListItemIcon sx={{ minWidth: 34 }}>
          <Logout fontSize="small" />
        </ListItemIcon>
        Cerrar sesión
      </MenuItem>
    </Menu>
  );

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          px: 2.5,
          py: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            bgcolor: "#f7f9ff",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            boxShadow: `0 14px 26px ${alpha(theme.palette.primary.main, 0.08)}`,
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src="/assets/icons/logo.png"
            alt="Convisas"
            sx={{
              width: "88%",
              height: "88%",
              objectFit: "contain",
            }}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} lineHeight={1.1}>
            CONVISAS
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Dashboard
          </Typography>
        </Box>
      </Box>

      <Divider />

      <List sx={{ px: 1.25, py: 1.5, flex: 1 }}>
        {visibleGroups.map((group) => (
          <Box key={group.title} sx={{ mb: 1.25 }}>
            <Typography
              variant="caption"
              sx={{
                px: 2,
                pb: 0.5,
                color: "text.secondary",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                display: "block",
              }}
            >
              {group.title}
            </Typography>

            {group.items.map((item) => {
              const isSelected =
                location.pathname === item.path ||
                location.pathname.startsWith(`${item.path}/`);

              return (
                <ListItemButton
                  key={item.path}
                  component={Link}
                  to={item.path}
                  onClick={isMobile ? () => setMobileOpen(false) : undefined}
                  selected={isSelected}
                  sx={{
                    mx: 0.5,
                    my: 0.5,
                    borderRadius: 2.5,
                    minHeight: 48,
                    px: 1.5,
                    color: isSelected
                      ? theme.palette.primary.main
                      : "text.primary",
                    bgcolor: isSelected
                      ? alpha(theme.palette.primary.main, 0.12)
                      : "transparent",
                    boxShadow: isSelected
                      ? `inset 3px 0 0 ${theme.palette.primary.main}`
                      : "none",
                    transition: "all 0.18s ease",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 38,
                      color: isSelected
                        ? theme.palette.primary.main
                        : "text.secondary",
                    }}
                  >
                    {React.cloneElement(item.icon as React.ReactElement<any>, {
                      sx: { fontSize: 20 },
                    })}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    slotProps={{
                      primary: {
                        sx: {
                          fontSize: "0.96rem",
                          fontWeight: isSelected ? 700 : 500,
                        },
                      },
                    }}
                  />
                </ListItemButton>
              );
            })}
          </Box>
        ))}
      </List>

      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <AppVersionBadge />
      </Box>
    </Box>
  );

  const sidebar = (
    <Box
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        borderRight: "1px solid rgba(15, 23, 42, 0.08)",
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      {drawerContent}
    </Box>
  );

  const desktopHeader = (
    <Box
      sx={{
        minHeight: 72,
        px: { xs: 2, md: 3 },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        bgcolor: "#ffffff",
        borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
        flexShrink: 0,
        position: "sticky",
        top: 0,
        zIndex: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <MenuIcon />
          </IconButton>
        )}
        <Box>
          <Breadcrumbs
            separator={<ChevronRightRounded fontSize="small" />}
            aria-label="dashboard breadcrumb"
            sx={{
              "& .MuiBreadcrumbs-separator": {
                color: "text.disabled",
              },
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {pageMeta?.section ?? "Dashboard"}
            </Typography>
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {pageMeta?.title ?? "Inicio"}
            </Typography>
          </Breadcrumbs>
          <Typography variant="caption" color="text.secondary">
            {pageMeta?.subtitle ?? "Resumen general"}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        onClick={handleUserMenuOpen}
        sx={{
          cursor: "pointer",
          px: 1,
          py: 0.5,
          borderRadius: 999,
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.06),
          },
        }}
      >
        <Avatar
          src={user?.photoURL || ""}
          alt={userLabel}
          sx={{
            width: 34,
            height: 34,
            bgcolor: theme.palette.primary.main,
            fontWeight: 700,
          }}
        >
          {!user?.photoURL && getInitials(userLabel)}
        </Avatar>
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Typography variant="body2" fontWeight={700} lineHeight={1.1}>
            {userLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Usuario
          </Typography>
        </Box>
        <ArrowDropDownRounded sx={{ color: "text.secondary" }} />
      </Stack>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        bgcolor: "#f5f7fb",
      }}
    >
      <ConfirmationDialog
        open={dialogOpen}
        title="Confirmar cierre de sesión"
        message="¿Estás seguro de que deseas cerrar sesión?"
        onClose={() => setDialogOpen(false)}
        onConfirm={handleLogout}
        confirmText="Cerrar sesión"
        cancelText="Cancelar"
      />

      {!isMobile && sidebar}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f5f7fb",
        }}
      >
        {desktopHeader}

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "auto",
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: drawerWidth,
              boxSizing: "border-box",
              bgcolor: "#ffffff",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {menuProfile}
    </Box>
  );
};

export default Dashboard;
